// File: frontend/src/context/AuthContext.js
// Version: 2.1 (2025-10-24) FINAL
// Description: AuthContext отвечает за аутентификацию, данные пользователя (user) и ссылки навигации (navLinks).
// Refactoring: Удалена загрузка dashboardData. Добавлено сохранение navigation_links из ответа /users/me.

// --- 1. ИМПОРТЫ ---
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import apiService from '../services/apiService'; // Сервис для взаимодействия с API
import { useNavigate } from 'react-router-dom'; // Хук для программной навигации
import { clearGeogridCache } from '../services/geogridCache';

// --- 2. СОЗДАНИЕ КОНТЕКСТА ---
export const AuthContext = createContext(null);

// Routes that render for anonymous visitors. On these we must NOT probe
// /users/me during bootstrap — an anonymous visitor has no session, and the
// probe produces a 401 + console noise on public-facing embeds (e.g. /widget).
const PUBLIC_BOOTSTRAP_PREFIXES = ['/widget', '/embed', '/login'];
const isPublicBootstrapRoute = () => {
    const path = window.location.pathname || '/';
    return PUBLIC_BOOTSTRAP_PREFIXES.some(
        (p) => path === p || path.startsWith(p + '/') || path.startsWith(p)
    );
};

// --- 3. ПРОВАЙДЕР КОНТЕКСТА ---
export const AuthProvider = ({ children }) => {
    // --- 4. СОСТОЯНИЕ (STATE) ---
    const [user, setUser] = useState(null); // Данные текущего пользователя
    const [navLinks, setNavLinks] = useState([]); // Ссылки навигации для текущего пользователя
    const [loading, setLoading] = useState(true); // Статус начальной загрузки/проверки токена
    const navigate = useNavigate();

    // --- 5. ФУНКЦИИ ---

    // Функция для получения данных пользователя и ссылок навигации с сервера.
    const fetchUserData = useCallback(async () => {
        try {
            // Запрашиваем данные текущего пользователя (/users/me).
            // ОЖИДАЕМ, что API вернет объект { ..., navigation_links: [...] }
            const responseData = await apiService.getMe();

            if (responseData) {
                // Извлекаем navigation_links из ответа
                const { navigation_links, ...userData } = responseData;

                setUser(userData); // Сохраняем остальные данные пользователя
                setNavLinks(navigation_links || []); // Сохраняем ссылки (или пустой массив)
                console.log("User data fetched:", userData); // Лог для отладки
                console.log("Nav links fetched:", navigation_links); // Лог для отладки
            } else {
                // Если API вернул пустой ответ (маловероятно при успехе)
                throw new Error("Received empty data from /users/me");
            }

        } catch (error) {
            // Bootstrap probe failed (anonymous, expired token, or network).
            // Do NOT trigger the apiService logout here: there is no session to end
            // and POST /auth/logout does not exist (would log a 404). Just clear state.
            console.error("Не удалось получить данные сессии пользователя:", error);
            setUser(null);       // Чистим пользователя
            setNavLinks([]);     // Чистим ссылки
        }
    }, []); // Пустой массив зависимостей - функция создается один раз

    // Функция для проверки токена и инициализации при загрузке приложения.
    const initializeAuth = useCallback(async () => {
        if (isPublicBootstrapRoute()) {
            // Public/embed route: anonymous by design. Set anonymous state with
            // zero network requests (eliminates the /users/me 401 on /widget).
            setUser(null);
            setNavLinks([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            await fetchUserData();
        } finally {
            setLoading(false);
        }
    }, [fetchUserData]);

    // Запускаем инициализацию один раз при монтировании провайдера
    useEffect(() => {
        initializeAuth();
    }, [initializeAuth]);

    // Функция для входа пользователя.
    const login = async (email, password) => {
        try {
            // Вызываем API для входа (сохраняет токен в localStorage)
            await apiService.login(email, password);
            // После успешного входа загружаем данные пользователя и ссылки
            setLoading(true); // Показываем загрузку на время fetchUserData
            await fetchUserData();
            setLoading(false);
            navigate('/dashboard'); // Перенаправляем на дашборд
        } catch (error) {
            console.error("Ошибка входа:", error);
            setUser(null); // Убедимся, что пользователь сброшен при ошибке входа
            setNavLinks([]);
            setLoading(false);
            throw error; // Пробрасываем ошибку для компонента Login
        }
    };

    // Функция для выхода пользователя.
    const logout = () => {
        apiService.logout(); // Чистим токен
        clearGeogridCache(); // Сбрасываем кэш замеров геосетки
        setUser(null);       // Чистим пользователя
        setNavLinks([]);     // Чистим ссылки
        navigate('/login');  // Перенаправляем на страницу входа
    };

    // --- 6. ЗНАЧЕНИЕ КОНТЕКСТА ---
    // Формируем объект value, который будет доступен через useAuth()
    const value = {
        user,                // Объект пользователя (или null)
        navLinks,            // Массив ссылок навигации (или [])
        login,               // Функция для входа
        logout,              // Функция для выхода
        updateUser: setUser, // [FIX] Expose setUser for profile updates
        isAuthenticated: !!user, // Флаг аутентификации
        loading              // Флаг начальной загрузки
    };

    // --- 7. РЕНДЕР ---
    // Предоставляем value всем дочерним компонентам
    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// --- 8. КАСТОМНЫЙ ХУК ---
// Упрощает использование контекста в компонентах
export const useAuth = () => {
    return useContext(AuthContext);
};

// --- Конец кода ---